import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, FormLabel, MenuItem, TextField } from '@material-ui/core'
import { AxiosError } from 'axios'
import React, { FunctionComponent, useState } from 'react'
import DataPluginParameterField from 'src/components/DataPluginParameterField'
import Spinner from 'src/components/Spinner'
import StreamSelector from 'src/components/StreamSelector'
import IDataComponent from 'src/models/IDataComponent'
import { ISetting } from 'src/models/IDataPlugin'
import IError from 'src/models/IError'
import IPipeline from 'src/models/IPipeline'
import IValidationError from 'src/models/IValidationError'

export interface IBackfillValues {
    // a property with no value is unset, rather than left as the pipeline has it
    properties: { [property: string]: string | null }
    timeout: number
    maxRetries: number
}

interface IBackfillDialogProps {
    // as `GET pipeline` returns it, which carries the properties and the discovered streams
    source: IPipeline
    onClose: () => void
    onRun: (values: IBackfillValues) => Promise<void>
}

const component = (pipeline: IPipeline, type: string) => pipeline._embedded!.dataComponents.find(dc => dc.dataPluginType === type)!

// a backfill is a copy of its source pipeline, with its own values for the settings that this dialog sets
export const backfillProperties = (source: IPipeline, own = source): IBackfillValues['properties'] => {
    const settingKeys = (pipeline: IPipeline) => [
        ['extractor', 'start_date'], ['extractor', 'end_date'], ['extractor', '_select'], ['loader', 'load_method'],
    ].map(([type, name]) => `${component(pipeline, type).name}.${name}`)
    const ownKeys = settingKeys(own)
    return settingKeys(source).reduce((ps, key, i) => ({ ...ps, [key]: own.properties[ownKeys[i]] || null }), {})
}

const BackfillDialog: FunctionComponent<IBackfillDialogProps> = ({ source, onClose, onRun }) => {
    const extractor = component(source, 'extractor')
    const loader = component(source, 'loader')
    const findSetting = (dc: IDataComponent, name: string): ISetting | undefined =>
        dc._embedded.dataplugin.settings.find(s => s.name === name)

    const dateSettings = ['start_date', 'end_date']
        .map(name => findSetting(extractor, name))
        .filter((s): s is ISetting => !!s)
    const loadMethodSetting = findSetting(loader, 'load_method')
    const loadMethodKey = `${loader.name}.load_method`
    const selectKey = `${extractor.name}._select`

    const [properties, setProperties] = useState(() => backfillProperties(source))
    const [timeout, setTimeoutSeconds] = useState(24 * 60 * 60)
    const [maxRetries, setMaxRetries] = useState(3)
    const [isRunning, setRunning] = useState(false)
    const [error, setError] = useState<string>()

    const setProperty = (name: string, value: string) => setProperties(p => ({ ...p, [name]: value || undefined } as typeof p))

    const handleRun = async () => {
        setRunning(true)
        setError(undefined)

        await onRun({
            properties: Object.keys(properties).reduce((ps, key) => ({ ...ps, [key]: properties[key] || null }), {}),
            timeout,
            maxRetries,
        })
            .then(onClose)
            .catch((e: AxiosError<IError | IValidationError[]>) => {
                const data = e.response?.data
                setError(Array.isArray(data) ? data[0].defaultMessage : data?.message || e.message)
                setRunning(false)
            })
    }

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Backfill {source.label}</DialogTitle>
            <DialogContent dividers>
                {dateSettings.map(setting =>
                    <DataPluginParameterField
                        key={setting.name}
                        fieldId={`${extractor.name}.${setting.name}`}
                        setting={setting}
                        name={`${extractor.name}.${setting.name}`}
                        property={extractor.properties[setting.name]}
                        currentValue={properties[`${extractor.name}.${setting.name}`]}
                        handleSettingsChange={setProperty} />)}
                {loadMethodSetting &&
                    <TextField
                        id="backfill-load-method"
                        select
                        fullWidth
                        margin="normal"
                        variant="outlined"
                        label={loadMethodSetting.label || loadMethodSetting.name}
                        value={properties[loadMethodKey] || loader.properties[loadMethodSetting.name] || loadMethodSetting.value || ''}
                        onChange={e => setProperty(loadMethodKey, e.target.value as string)}>
                        {loadMethodSetting.options?.map(o =>
                            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                    </TextField>}
                <FormControl component="fieldset" fullWidth margin="normal">
                    <FormLabel component="legend">Streams</FormLabel>
                    {extractor.streams?.length
                        ? <StreamSelector
                            streams={extractor.streams}
                            rules={properties[selectKey] && JSON.parse(properties[selectKey]!)}
                            onRulesChange={rules => setProperty(selectKey, rules.length ? JSON.stringify(rules) : '')} />
                        : <FormHelperText>No streams discovered. Verify the pipeline to discover them.</FormHelperText>}
                </FormControl>
                <TextField
                    fullWidth
                    type="number"
                    margin="normal"
                    variant="outlined"
                    id="backfill-timeout"
                    inputProps={{ min: 1 }}
                    label="Timeout (seconds)"
                    // a run given no timeout stops at the default of 1 hour
                    error={timeout < 1}
                    helperText={timeout < 1 ? 'Enter a timeout of at least 1 second' : undefined}
                    value={timeout || ''}
                    onChange={e => setTimeoutSeconds(Math.max(0, Number(e.target.value)))} />
                <TextField
                    fullWidth
                    type="number"
                    margin="normal"
                    variant="outlined"
                    id="backfill-max-retries"
                    inputProps={{ min: 0 }}
                    label="Max retries"
                    value={maxRetries}
                    onChange={e => setMaxRetries(Math.max(0, Number(e.target.value)))} />
                {error && <FormHelperText error>{error}</FormHelperText>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isRunning}>Cancel</Button>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={isRunning || timeout < 1}
                    onClick={handleRun}>
                    <Spinner active={isRunning}>Run backfill</Spinner>
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default BackfillDialog
